import React, { useRef, useEffect, useState } from "react";
import { BrowserMultiFormatReader, NotFoundException } from "@zxing/library";
import { styles, createScanHints, beschreibeKamera } from "./MultiFormatReaderUtils";
import { ViewFinder } from "./ViewFinder";

const MultiFormatReader = ({ onScan, constraints }) => {
  const videoRef = useRef(null);

  // onScan wurde früher per useCallback(onScan, []) eingefroren. Damit lief der
  // Scanner dauerhaft gegen den Handler des allerersten Renders und sah spätere
  // Props/Redux-Stände nicht mehr - der Grund für die multiscan-/erfassteT-Refs
  // in QRCodeScannerDialog. Über eine Ref bleibt die Identität stabil (der
  // Effekt startet die Kamera nicht neu), der Aufruf trifft aber den aktuellen
  // Handler.
  const onScanRef = useRef(onScan);
  onScanRef.current = onScan;

  const codeReader = useRef(null);
  if (codeReader.current === null) {
    // Zweiter Parameter ist timeBetweenScansMillis. Weitere Argumente nimmt der
    // Konstruktor nicht - die Formatliste muss als Hints-Map rein.
    codeReader.current = new BrowserMultiFormatReader(createScanHints(), 500);
  }
  const reader = codeReader.current;

  // Bewusst nur deviceId und facingMode als Dependencies: constraints ist in
  // beiden Dialogen ein Objektliteral und damit bei jedem Render neu. Stand es
  // in der Liste, riss der Effekt bei jedem Render die Kamera ab und startete
  // sie neu.
  // facingMode geht nicht an getUserMedia: decodeFromVideoDevice baut seine
  // Constraints ausschließlich aus der deviceId. Die Kameraseite wird von den
  // Dialogen über getDeviceId() in eine deviceId übersetzt; hier steht
  // facingMode nur als Dependency (Neustart beim Umschalten) und für die
  // Spiegelung der Vorschau.
  const deviceId = constraints.deviceId;
  const facingMode = constraints.facingMode || "environment";

  // Diagnosezeile, siehe beschreibeKamera. Gelesen wird, sobald das Video seine
  // Abmessungen kennt, und erneut bei jeder Änderung (Drehen des Telefons) -
  // erst dann stehen in getSettings die tatsächlich gelieferten Werte.
  const [kameraInfo, setKameraInfo] = useState(null);
  const leseKameraInfo = () => {
    const track = videoRef.current?.srcObject?.getVideoTracks?.()[0];
    setKameraInfo(beschreibeKamera(track));
  };

  useEffect(() => {
    let abgebrochen = false;

    const timer = setTimeout(() => {
      if (abgebrochen || !videoRef.current) {
        return;
      }
      // Beim Kamerawechsel nicht die Werte der vorigen stehen lassen
      setKameraInfo(null);
      reader.reset();
      reader
        .decodeFromVideoDevice(deviceId, videoRef.current, (result, err) => {
          if (abgebrochen) {
            return;
          }
          // Ohne Inhalt gibt es nichts zu melden. Sonst landet ein leerer
          // Treffer in den Dialogen und löst dort einen "nicht erkannt"-Toast
          // aus, obwohl gar nichts gescannt wurde.
          const text = typeof result?.text === "string" ? result.text.trim() : "";
          if (result && !text) {
            console.debug("Scan-Treffer ohne Inhalt verworfen", result);
            return;
          }
          if (result) {
            onScanRef.current(result);
          } else if (err && !(err instanceof NotFoundException)) {
            console.error(err);
          }
        })
        .catch((err) => {
          if (!abgebrochen) {
            console.error(err);
          }
        });
    }, 1024); // Verzögerung, bis das <video> im Dialog wirklich sichtbar ist

    return () => {
      // Das setTimeout wurde früher nicht abgeräumt: bei schnell aufeinander
      // folgenden Effektläufen standen mehrere Starts gleichzeitig an.
      abgebrochen = true;
      clearTimeout(timer);
      reader.reset();
    };
  }, [reader, deviceId, facingMode]);

  return (
    <>
      <div
        style={{
          ...styles.container
        }}
      >
        <ViewFinder />
        <video
          ref={videoRef}
          onLoadedMetadata={leseKameraInfo}
          onResize={leseKameraInfo}
          style={{
            ...styles.video,
            transform: facingMode === "user" ? "scaleX(-1)" : undefined,
          }}
        />
      </div>
      {kameraInfo && (
        <div className="small text-body-secondary mt-1 text-break" data-testid="kamera-diagnose">
          {kameraInfo}
        </div>
      )}
    </>
  );
};

export default MultiFormatReader;
